import { useCallback, useEffect, useRef, useState } from "react";
import { generationsApi } from "../api/endpoints";
import type { GenerationStatusPayload } from "../types/api";

const POLL_INTERVAL_MS = 1500;
/** Comfortably past the provider's own 180s timeout, before the server reaper. */
const MAX_WAIT_MS = 4 * 60 * 1000;

export interface GenerationProgress {
  status: GenerationStatusPayload["status"] | "idle";
  result: GenerationStatusPayload | null;
  error: string | null;
  /** 0–1, eased. There is no real progress signal from the provider, so this is
      a time-based estimate purely to keep the waiting screen alive. */
  progress: number;
}

/**
 * Polls a generation to completion.
 *
 * The API is async by default (202 + handle), so the booth's processing screen
 * lives entirely on this hook.
 */
export function useGenerationPolling(slug: string) {
  const [state, setState] = useState<GenerationProgress>({
    status: "idle", result: null, error: null, progress: 0,
  });

  const timer = useRef<number | null>(null);
  const startedAt = useRef<number>(0);
  const cancelled = useRef(false);

  const stop = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => () => { cancelled.current = true; stop(); }, [stop]);

  const track = useCallback((generationId: number) => {
    cancelled.current = false;
    startedAt.current = Date.now();
    setState({ status: "pending", result: null, error: null, progress: 0.02 });

    const tick = async () => {
      if (cancelled.current) return;

      const elapsed = Date.now() - startedAt.current;
      if (elapsed > MAX_WAIT_MS) {
        setState((previous) => ({
          ...previous,
          status: "failed",
          error: "This is taking longer than expected. Please try again.",
        }));
        return;
      }

      try {
        const payload = await generationsApi.status(slug, generationId);
        if (cancelled.current) return;

        if (payload.status === "completed") {
          setState({ status: "completed", result: payload, error: null, progress: 1 });
          return;
        }
        if (payload.status === "failed") {
          setState({
            status: "failed", result: payload, progress: 1,
            error: payload.error ?? "Generation failed. Please try again.",
          });
          return;
        }

        // Asymptotic: approaches but never reaches 95% until the real result
        // lands, so the bar never sits full while the guest still waits.
        setState({
          status: payload.status, result: payload, error: null,
          progress: 0.95 * (1 - Math.exp(-elapsed / 25000)),
        });
        timer.current = window.setTimeout(tick, POLL_INTERVAL_MS);
      } catch (error) {
        if (cancelled.current) return;
        // A transient network blip shouldn't kill the whole wait — retry until
        // the overall deadline.
        timer.current = window.setTimeout(tick, POLL_INTERVAL_MS * 2);
        void error;
      }
    };

    void tick();
  }, [slug]);

  const reset = useCallback(() => {
    cancelled.current = true;
    stop();
    setState({ status: "idle", result: null, error: null, progress: 0 });
  }, [stop]);

  return { ...state, track, reset };
}
